import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, ZoomIn, X, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import PriceSummary from './PriceSummary';

function OptionCard({ option, isSelected, onClick, isMultiselect, priceMap, sectionId }) {
  const [zoomed, setZoomed] = useState(false);
  const compositeKey = sectionId ? `${sectionId}__${option.id}` : option.id;
  const displayPrice = priceMap?.[compositeKey] !== undefined ? priceMap[compositeKey] : (priceMap?.[option.id] !== undefined ? priceMap[option.id] : option.price);

  return (
    <>
      <button
        onClick={onClick}
        className={`relative flex flex-col border-2 transition-all rounded-[6px] overflow-hidden text-left w-full md:h-full ${
          isSelected ? 'border-foreground' : 'border-border hover:border-foreground/40'
        }`}
      >
        {option.img ? (
           <div className="relative w-full aspect-square overflow-hidden bg-card">
             <img src={option.img} alt={option.label} className="w-full h-full object-cover object-center" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
              <p className="font-sans text-xs tracking-widest uppercase text-white font-medium">{option.label}</p>
            </div>
            <div
              onClick={(e) => { e.stopPropagation(); setZoomed(true); }}
              className="absolute top-2 right-2 p-1 bg-black/50 rounded-sm hover:bg-black/70 transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
            >
              <ZoomIn size={13} className="text-white" />
            </div>
            {isSelected && (
              <div className={`absolute top-2 left-2 w-5 h-5 flex items-center justify-center ${
                isMultiselect ? 'bg-foreground rounded-sm' : 'bg-foreground rounded-full'
              }`}>
                <Check size={11} className="text-background" />
              </div>
            )}
          </div>
        ) : (
          <div
            className="w-full aspect-square flex items-center justify-center"
            style={{ backgroundColor: option.color || '#e0d8ce' }}
          >
            {isSelected && (
              <div className="w-6 h-6 rounded-full bg-foreground flex items-center justify-center">
                <Check size={12} className="text-background" />
              </div>
            )}
          </div>
        )}
        <div className="px-3 py-3 bg-background flex flex-col gap-1">
          {!option.img && (
            <p className="font-serif text-sm text-foreground leading-tight mb-0.5">{option.label}</p>
          )}
          <p className="font-sans text-xs tracking-widest uppercase text-primary font-semibold">
            {displayPrice === 0 ? 'Inkl.' : displayPrice > 0 ? `+${displayPrice.toLocaleString('nb-NO')} NOK` : `${displayPrice.toLocaleString('nb-NO')} NOK`}
          </p>
          {option.description && (
            <p className="font-serif text-sm text-foreground/60 leading-snug mt-0.5">{option.description}</p>
          )}
          {option.learnMoreLink && (
            <Link to={option.learnMoreLink} className="block mt-2 text-xs tracking-widest uppercase font-semibold text-[#2d5a3d] hover:text-[#2d5a3d]/70 transition-colors">
              LÆR MER →
            </Link>
          )}
          {option.isCustom && isSelected && (
            <input
              type="text"
              placeholder={option.inputPlaceholder || ''}
              className="mt-2 w-full h-8 px-2 border border-border bg-background font-serif text-sm focus:outline-none focus:border-foreground rounded-sm"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      </button>
      {zoomed && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setZoomed(false)}>
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={option.img} alt={option.label} className="w-full h-auto object-contain rounded-sm" />
            <button onClick={() => setZoomed(false)} className="absolute top-2 right-2 p-2 bg-white/10 hover:bg-white/20 rounded-sm transition-colors">
              <X size={20} className="text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function ConfiguratorStepSlider({ config, onUpdate, steps, totalPrice, basePrice, onSubmit, priceMap = {} }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  
  // Efekt do nasłuchiwania zmian cen w bazie
  useEffect(() => {
    // Nasłuch zmian cen w czasie rzeczywistym
    const unsubscribe = base44.entities.ConfigurationPrice.subscribe((event) => {
      if (event.type === 'create' || event.type === 'update') {
        // Trigger parent component to refetch prices
        window.dispatchEvent(new CustomEvent('pricesUpdated', { detail: { optionId: event.data.optionId, price: event.data.price } }));
      }
    });
    return () => unsubscribe?.();
  }, []);

  const goTo = (index) => {
    setDirection(index > currentStep ? 1 : -1);
    setCurrentStep(index);
  };

  const handleBack = () => { if (currentStep > 0) goTo(currentStep - 1); };
  const handleSelect = (sectionId, optionId) => {
    onUpdate(sectionId, optionId);
    
    // Track sauna component selection
    const selectedOption = section.options.find(o => o.id === optionId);
    base44.analytics.track({
      eventName: 'sauna_component_selected',
      properties: {
        component_id: sectionId,
        component_name: section.label,
        option_id: optionId,
        option_label: selectedOption?.label || '',
        option_price: selectedOption?.price || 0,
        step_number: currentStep + 1,
        total_steps: steps.length,
        is_multiselect: section.multiselect
      }
    });
    
    if (!section.multiselect && currentStep < steps.length - 1) {
      setTimeout(() => goTo(currentStep + 1), 180);
    }
  };

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const firstChild = scrollContainerRef.current.children[0];
      const scrollAmount = firstChild ? firstChild.offsetWidth + 12 : 280;
      scrollContainerRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const section = steps[currentStep];
  const selectedValue = config[section.id];
  const selectedIds = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
  const isMultiselectSection = section.multiselect;

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir < 0 ? 60 : -60, opacity: 0 }),
  };

  return (
    <div className="flex flex-col md:flex-row border border-border rounded-[6px] overflow-hidden">
      {/* Sidebar */}
      <div className="md:w-56 shrink-0 border-b md:border-b-0 md:border-r border-border bg-card flex flex-col">

        {/* Mobile accordion header */}
        <div className="md:hidden">
          <button
            onClick={() => setMobileNavOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 bg-card border-b border-border"
          >
            <div className="flex items-center gap-3">
              <span className="font-sans text-xs text-muted-foreground">Steg {currentStep + 1} / {steps.length}</span>
              <span className="font-sans text-xs tracking-widest uppercase font-semibold text-foreground">{steps[currentStep].label}</span>
            </div>
            {mobileNavOpen ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
          </button>
          {mobileNavOpen && (
            <nav className="border-b border-border">
              {steps.map((step, index) => {
                const isCurrent = index === currentStep;
                return (
                  <button
                    key={step.id}
                    onClick={() => { goTo(index); setMobileNavOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      isCurrent ? 'bg-foreground text-background' : 'hover:bg-border/30 text-foreground/70'
                    }`}
                  >
                    <span className="text-xs font-semibold text-muted-foreground w-4 text-center flex-shrink-0">{index + 1}</span>
                    <span className="font-sans text-xs tracking-widest uppercase leading-tight">{step.label}</span>
                  </button>
                );
              })}
            </nav>
          )}
        </div>

        <nav className="hidden md:block overflow-y-auto py-2">
          {steps.map((step, index) => {
            const isDone = index < currentStep;
            const isCurrent = index === currentStep;
            return (
              <button
                key={step.id}
                onClick={() => goTo(index)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  isCurrent ? 'bg-foreground text-background' : 'hover:bg-border/30 text-foreground/70'
                }`}
              >
                <span className="text-xs font-semibold text-muted-foreground w-4 text-center flex-shrink-0">
                  {index + 1}
                </span>
                <span className="font-sans text-xs tracking-widest uppercase leading-tight">{step.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Price summary */}
        <div className="p-4 bg-foreground text-background">
          <p className="font-sans text-[9px] tracking-widest uppercase text-background/60 mb-2">MODELL</p>
          <p className="font-sans text-[10px] tracking-widest uppercase text-background/80 mb-3">LYKKE SAUNA</p>
          <div className="border-t border-background/20 pt-3">
            <p className="font-sans text-[9px] tracking-widest uppercase text-background/60">Bruttopris (inkl. mva 25%)</p>
            <p className="font-serif text-xl text-background mt-0.5">{(totalPrice || 0).toLocaleString('nb-NO')} NOK</p>
          </div>
        </div>

        {/* Selected options accordion */}
        <div className="border-t border-background/20">
          <button
            onClick={() => setSummaryOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 bg-foreground/90 text-background hover:bg-foreground/80 transition-colors"
          >
            <span className="font-sans text-[9px] tracking-widest uppercase text-background/60">Valgte opsjoner</span>
            <ChevronDown size={13} className={`text-background/50 transition-transform ${summaryOpen ? 'rotate-180' : ''}`} />
          </button>
          {summaryOpen && (
            <div className="bg-foreground/95 px-4 pb-3 space-y-2">
              {steps.filter(s => !s.isSummary).map(step => {
                const sel = step.options.find(o => o.id === config[step.id]);
                if (!sel) return null;
                return (
                  <div key={step.id} className="border-t border-background/10 pt-2">
                    <p className="font-sans text-[8px] tracking-widest uppercase text-background/40">{step.label}</p>
                    <p className="font-serif text-sm text-background/80 leading-tight mt-0.5">{sel.label}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {!section.hideHeader && (
          <div className="px-6 py-5 border-b border-border">
            <div className="flex items-baseline gap-3">
              <h2 className="font-heading text-2xl font-light text-foreground">{section.label}</h2>
              <p className="font-sans text-xs tracking-widest uppercase text-muted-foreground">Steg {currentStep} / {steps.length - 1}</p>
            </div>
            {section.description && (
              <p className="font-serif text-base text-foreground/60 mt-1">{section.description}</p>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {section.isSummary ? (
            <PriceSummary
              config={config}
              totalPrice={totalPrice}
              steps={steps.filter(s => !s.isSummary)}
              onSubmit={onSubmit}
              defaultOpen
            />
          ) : (
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentStep}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                {section.options.length === 0 && (section.image || section.images) ? (
                  <div className="w-full h-full flex justify-center">
                    {section.images ? (
                      <div className="flex gap-6 h-full max-w-full">
                        {section.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`${section.label} ${idx + 1}`}
                            className="flex-shrink-0 w-96 max-h-[60vh] object-contain rounded-[6px]"
                          />
                        ))}
                      </div>
                    ) : (
                      <img
                        src={section.image}
                        alt={section.label}
                        className="w-full max-h-[60vh] object-contain rounded-[6px]"
                      />
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => handleScroll('left')}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 md:hidden p-2 bg-background/80 hover:bg-background transition-colors"
                    >
                      <ChevronLeft size={20} className="text-foreground" />
                    </button>
                    <div ref={scrollContainerRef} className={`flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory ${section.fixedWidth ? '' : 'md:overflow-x-visible md:h-full justify-center'}`} style={{scrollbarWidth: 'thin', scrollbarColor: 'hsl(30 25% 30%) hsl(30 15% 88%)'}}>
                      {section.options.map(option => (
                        <div key={option.id} className={`flex-shrink-0 w-full md:w-64 snap-start ${section.fixedWidth ? '' : 'md:flex-shrink md:flex-1 md:min-w-0 md:flex md:flex-col'}`}>
                          <OptionCard
                            option={option}
                            isSelected={isMultiselectSection ? selectedIds.includes(option.id) : selectedIds[0] === option.id}
                            onClick={() => handleSelect(section.id, option.id)}
                            isMultiselect={section.multiselect}
                            priceMap={priceMap}
                            sectionId={section.id}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => handleScroll('right')}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 md:hidden p-2 bg-background/80 hover:bg-background transition-colors"
                    >
                      <ChevronRight size={20} className="text-foreground" />
                    </button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Bottom navigation */}
        <div className="border-t border-border px-6 py-4 flex items-center justify-between bg-background">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 py-2.5 border border-border text-foreground text-sm font-sans tracking-widest uppercase disabled:opacity-30 disabled:cursor-not-allowed hover:bg-card transition-colors rounded-sm"
          >
            <ChevronLeft size={15} />
            Tilbake
          </button>
          <span className="font-sans text-sm text-muted-foreground">
            {currentStep + 1} / {steps.length}
          </span>
          {currentStep < steps.length - 1 ? (
            <button
              onClick={() => goTo(currentStep + 1)}
              className="flex items-center gap-2 px-4 py-2.5 border border-foreground bg-foreground text-background text-sm font-sans tracking-widest uppercase hover:bg-foreground/90 transition-colors rounded-sm"
            >
              Neste
              <ChevronRight size={15} />
            </button>
          ) : (
            <div className="w-24" />
          )}
        </div>
      </div>
    </div>
  );
}